import { AuthError, AUTH_ERROR_CODE } from '@shared/auth/errors';
import { requestRefresh } from '@shared/auth/refresh-request';
import { isImpersonating, tokenStore } from '@shared/auth/token.store';
import type { User } from '@shared/types/auth';

/**
 * The localStorage key used by AuthProvider to persist the current user.
 * Defined here so both AuthProvider and route-level guards share one source of truth.
 */
export const USER_STORAGE_KEY = 'auth-user';

export function userStorage(): Storage {
  return isImpersonating() ? sessionStorage : localStorage;
}

/**
 * Reads the current user from localStorage without going through React context.
 *
 * Use this only in contexts that cannot access React hooks — specifically in
 * TanStack Router `beforeLoad` callbacks. Everywhere else, use `useAuth()`.
 */
export function getStoredUser(): User | null {
  try {
    const raw = userStorage().getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

/**
 * Obtains a fresh access token via the httpOnly refresh-token cookie and stores
 * it in tokenStore. Designed for use in TanStack Router's `beforeLoad`.
 *
 * Shares ONE in-flight `/api/refresh` request with every other refresher
 * (AuthProvider silent refresh, the 60s poll, the urql/ws exchanges) via the
 * dependency-free `requestRefresh` module — so the router guard and AuthProvider
 * no longer fire two competing refreshes on a page reload and race the
 * single-use refresh cookie. `requestRefresh` is kept router-free to avoid an
 * import cycle (auth-route.ts is imported by router.tsx).
 */
export async function bootstrapToken(): Promise<void> {
  const res = await requestRefresh();

  if (res.status === 401 || res.status === 403) {
    throw new AuthError(AUTH_ERROR_CODE.UNAUTHENTICATED);
  }
  if (!res.ok) {
    throw new AuthError(AUTH_ERROR_CODE.SERVER_UNAVAILABLE);
  }

  const data = (await res.json()) as {
    token?: string;
    accessTokenTTL?: number;
  };
  if (!data.token) {
    throw new AuthError(AUTH_ERROR_CODE.TOKEN_NOT_RECEIVED);
  }

  tokenStore.getState().setAccessToken(data.token, data.accessTokenTTL);
}
